import axiosInstance from "../../axios";
import { PARTY_SCOPE } from "./apiContract";
import { enrichCreditNotesWithUsage } from "./enrichCreditNoteUsage";
import { getCreditNoteOriginCalculationDummy } from "./creditNoteOriginCalculation";
import {
  MOCK_ADJUSTMENTS_BY_CREDIT_NOTE_ID,
  MOCK_BUYER_CREDIT_NOTES,
  MOCK_DEALER_CREDIT_NOTES,
} from "./fixtures";

const creditNoteMocksEnabled = () =>
  process.env.REACT_APP_USE_CREDIT_NOTE_MOCKS === "true";

const delay = (ms = 120) => new Promise((r) => setTimeout(r, ms));

function notesForScope(partyScope) {
  if (partyScope === PARTY_SCOPE.DIRECT_BUYER) {
    return [...MOCK_BUYER_CREDIT_NOTES];
  }
  return [...MOCK_DEALER_CREDIT_NOTES];
}

export async function getCreditNotes({ partyScope, status, identifier } = {}) {
  if (creditNoteMocksEnabled()) {
    await delay();
    let list = notesForScope(partyScope);
    if (status) {
      list = list.filter((n) => n.status === status);
    }
    if (identifier) {
      const q = String(identifier).trim().toLowerCase();
      list = list.filter((n) =>
        String(n.identifier || "")
          .toLowerCase()
          .includes(q)
      );
    }
    return {
      credit_notes: enrichCreditNotesWithUsage(
        list,
        MOCK_ADJUSTMENTS_BY_CREDIT_NOTE_ID
      ),
    };
  }

  const params = {};
  if (partyScope) params.party_scope = partyScope;
  if (status) params.status = status;
  if (identifier) params.identifier = identifier;

  const res = await axiosInstance.get("/store/credit_notes", { params });
  return res?.data?.data ?? res?.data ?? { credit_notes: [] };
}

export async function getCreditNote(creditNoteId) {
  if (creditNoteMocksEnabled()) {
    await delay();
    const all = [...MOCK_DEALER_CREDIT_NOTES, ...MOCK_BUYER_CREDIT_NOTES];
    const note = all.find((n) => String(n.id) === String(creditNoteId));
    if (!note) {
      const err = new Error("Credit note not found");
      err.response = { status: 404 };
      throw err;
    }
    const adjustments =
      MOCK_ADJUSTMENTS_BY_CREDIT_NOTE_ID[note.id] ?? [];
    const adjusted_amount = adjustments.reduce(
      (s, a) => s + Number(a.value || 0),
      0
    );
    return {
      credit_note: note,
      adjustments,
      adjusted_amount,
      remaining_balance: note.balance,
    };
  }

  const res = await axiosInstance.get(`/store/credit_notes/${creditNoteId}`);
  return res?.data?.data ?? res?.data;
}

export async function createAdjustment({ credit_note_id, project_id, value }) {
  if (creditNoteMocksEnabled()) {
    await delay();
    return {
      adjustment: {
        id: Date.now(),
        credit_note_id,
        project_id,
        value,
        adjusted_at: new Date().toISOString(),
      },
    };
  }

  const res = await axiosInstance.post("/store/adjustments", {
    credit_note_id,
    project_id,
    value,
  });
  return res?.data?.data ?? res?.data;
}

export async function getProjectAdjustments(projectId) {
  if (creditNoteMocksEnabled()) {
    await delay();
    const all = Object.values(MOCK_ADJUSTMENTS_BY_CREDIT_NOTE_ID).flat();
    return {
      adjustments: all.filter(
        (a) => String(a.project_id) === String(projectId)
      ),
    };
  }

  const res = await axiosInstance.get(
    `/store/projects/${projectId}/adjustments`
  );
  return res?.data?.data ?? res?.data ?? { adjustments: [] };
}

export async function hasAnyCreditNotes(partyScope) {
  const { credit_notes } = await getCreditNotes({ partyScope });
  return Array.isArray(credit_notes) && credit_notes.length > 0;
}

/**
 * How this credit note was calculated / where it came from.
 */
export async function getCreditNoteOriginCalculation(creditNoteId, creditNoteHint) {
  if (creditNoteMocksEnabled()) {
    await delay();
    let note = creditNoteHint;
    if (!note) {
      const all = [...MOCK_DEALER_CREDIT_NOTES, ...MOCK_BUYER_CREDIT_NOTES];
      note = all.find((n) => String(n.id) === String(creditNoteId));
    }
    if (!note) {
      const err = new Error("Credit note not found");
      err.response = { status: 404 };
      throw err;
    }
    return {
      origin: getCreditNoteOriginCalculationDummy(note),
    };
  }

  const res = await axiosInstance.get(
    `/store/credit_notes/${creditNoteId}/origin`
  );
  const data = res?.data?.data ?? res?.data;
  return { origin: data?.origin ?? null };
}
